import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Phone, Mail, MapPin, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Overline, NewsCard } from "@/components/Shared";
import { useSettings } from "@/lib/settings";

const HERO_VIDEO = "/hero-video.mp4";
const HERO_POSTER = "/hero-poster.jpg";

const MARQUEE_ITEMS = ["Efficienza Energetica A4", "Zero Gas Metano", "Casa su Misura", "Dal 1969", "Mezzo Secolo di Eccellenza", "Sartorialità Edilizia"];

const CHAPTERS = [
  { n: "01", title: "La Fondazione", text: "1969. Marcello Lattanzi avvia la sua ditta individuale di impresario edile, gettando le fondamenta di una storia lunga mezzo secolo." },
  { n: "02", title: "La Famiglia", text: "Mirella cura l'amministrazione e le pratiche burocratiche. I figli Daniele e Marco portano innovazione nelle tecniche di realizzazione." },
  { n: "03", title: "Casa su Misura", text: "Ogni abitazione è personalizzabile secondo esigenze e gusti del cliente, per un riscontro positivo anche a distanza di molti anni." },
  { n: "04", title: "Classe Energetica A4", text: "Immobili all'avanguardia, senza gas metano: risparmio nel tempo, alta funzionalità e tecnologie al passo con i tempi." },
];

export default function Home() {
  const [novita, setNovita] = useState([]);
  const { settings } = useSettings();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 160]);

  useEffect(() => {
    api.get("/projects", { params: { kind: "novita" } }).then((r) => setNovita(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <main>
      {/* HERO */}
      <section className="relative flex h-screen items-center overflow-hidden bg-charcoal">
        <motion.div style={{ y: heroY }} className="absolute inset-0 scale-110">
          <video
            data-testid="hero-video"
            className="h-full w-full object-cover"
            poster={HERO_POSTER}
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={HERO_VIDEO} type="video/mp4" />
            <source src="/hero-video.webm" type="video/webm" />
          </video>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 via-55% to-white/90 to-95%" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 via-45% to-transparent" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-white [text-shadow:0_1px_12px_rgba(0,0,0,0.7)]">Impresa Edile · Fermo · Dal 1969</p>
          </motion.div>
          <h1 className="mt-6 font-serif font-light leading-[1.02] tracking-tight text-white [text-shadow:0_2px_30px_rgba(0,0,0,0.6)]">
            {["Costruiamo", "la qualità", "a casa tua."].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.3 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                  className={`block text-4xl sm:text-5xl lg:text-6xl ${i === 1 ? "italic text-gold" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white [text-shadow:0_1px_16px_rgba(0,0,0,0.7)]"
          >
            Da mezzo secolo realizziamo complessi residenziali e commerciali con standard qualitativi
            medio-alti, immobili in Classe Energetica A4 e la cura sartoriale di un'impresa di famiglia.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/lavori?tab=novita"
              data-testid="hero-cta-novita"
              className="group flex items-center gap-3 bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-brand"
            >
              Scopri le Novità
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/lavori?tab=svolti"
              data-testid="hero-cta-lavori"
              className="flex items-center gap-3 border border-white/60 px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-gold hover:text-gold"
            >
              Lavori già svolti
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        >
          <ArrowDown className="h-5 w-5 animate-bounce text-gold" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-y border-slate-200 bg-obsidian py-4">
        <div className="animate-marquee flex w-max items-center gap-10">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center gap-10 whitespace-nowrap font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
              {item} <span className="text-gold">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* NOVITÀ IN CANTIERE */}
      <section data-testid="novita-teaser-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <Overline>In cantiere ora</Overline>
            <h2 className="mt-4 font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Le novità prendono <span className="italic text-brand">forma</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/lavori" data-testid="teaser-link-lavori" className="group flex items-center gap-2 border border-slate-300 px-6 py-3 text-xs uppercase tracking-[0.25em] text-slate-700 transition-all duration-300 hover:border-gold hover:text-gold">
              I Nostri Lavori <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        {novita.length === 0 ? (
          <p className="mt-12 border border-dashed border-slate-300 py-16 text-center text-sm text-slate-400">
            Nessun cantiere attivo al momento. Torna presto a trovarci.
          </p>
        ) : (
          <div data-testid="novita-teaser-grid" className="mt-12 grid gap-6 md:grid-cols-3">
            {novita.map((p, i) => (
              <NewsCard key={p.id} p={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* MANIFESTO NUMERATO */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <Reveal>
            <Overline>Il manifesto</Overline>
            <h2 className="mt-4 max-w-2xl font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Quattro capitoli, <span className="italic text-brand">una sola firma</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1} className="bg-white">
                <div className="group h-full p-8 transition-colors duration-500 hover:bg-obsidian">
                  <span className="font-mono text-sm text-gold">{c.n}</span>
                  <h3 className="mt-6 font-serif text-xl text-slate-900 sm:text-2xl">{c.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate-500">{c.text}</p>
                  <div className="mt-6 h-px w-10 bg-gold/50 transition-all duration-500 group-hover:w-full group-hover:bg-gold" />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/storia" data-testid="home-link-storia" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-600 transition-colors hover:text-gold">
              Leggi la nostra storia <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CONTATTI */}
      <section id="contatti" data-testid="contatti-section" className="border-t border-slate-200 bg-obsidian">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <Reveal>
            <Overline>Parliamone</Overline>
            <h2 className="mt-4 font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Serietà e professionalità <span className="italic text-brand">al vostro servizio</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Phone, label: "Telefono e Fax", value: settings.phone, href: `tel:+39${settings.phone.replace(/\s/g, "")}`, tid: "contact-phone" },
              { icon: Users, label: "Diretti", value: settings.mobiles, tid: "contact-mobiles" },
              { icon: Mail, label: "E-mail", value: settings.email, href: `mailto:${settings.email}`, tid: "contact-email" },
              { icon: MapPin, label: "Sede", value: `${settings.address} · P.I. ${settings.piva}`, tid: "contact-address" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                <div data-testid={c.tid} className="group h-full border border-slate-200 bg-white p-7 transition-colors duration-500 hover:border-gold/60">
                  <c.icon className="h-5 w-5 text-gold" />
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="mt-2 block text-sm leading-relaxed text-slate-800 transition-colors hover:text-gold">{c.value}</a>
                  ) : (
                    <p className="mt-2 text-sm leading-relaxed text-slate-800">{c.value}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
